import { Modal } from "@components";
import { useDocumentSignatureContext } from "@contexts";
import { styled } from "@mui/material";
import { useSessionPersistentCountdown } from "@hooks";
import { useEffect } from "react";
import { blockedPinDetails } from "../hooks/onDocumentSignatureError";

// Used before the modal opens, when there is no message yet.
const DEFAULT_BLOCKING_TIME_IN_SECONDS = 30 * 60;

export const BLOCKED_PIN_REASON =
  "Se bloqueó después de varios intentos con un PIN incorrecto.";

const BoldText = styled("span")({
  fontWeight: 600,
});

interface Props {
  onReEnlistment: () => void;
  isUnenrolling?: boolean;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
}

export function BlockedPinDocumentSignatureErrorModal({
  onReEnlistment,
  isUnenrolling = false,
  onPrimaryAction,
  onSecondaryAction,
}: Props) {
  const {
    showBlockedPinErrorModal,
    esignErrorMessage,
    setShowInvalidPinErrorModal,
    setShowBlockedPinErrorModal,
  } = useDocumentSignatureContext();

  // Countdown for blocked PIN, only when the provider said how long it lasts
  const { blockingTimeInSeconds, providerText } = blockedPinDetails(esignErrorMessage);

  const { formattedTime, start, clear, setUnblockTimestamp } =
    useSessionPersistentCountdown({
      initialTimeInSeconds: blockingTimeInSeconds ?? DEFAULT_BLOCKING_TIME_IN_SECONDS,
      autoStart: false,
      sessionStorageKey: "pinUnblockTimestamp",
      onComplete: () => {
        setShowBlockedPinErrorModal(false);
        setShowInvalidPinErrorModal(true);
      },
    });

  useEffect(
    function startCountdownWhenModalOpens() {
      if (showBlockedPinErrorModal) {
        // Also drops a countdown an earlier block left in sessionStorage, so it
        // cannot reopen the PIN screen while this block lasts.
        clear();

        if (blockingTimeInSeconds === null) {
          return;
        }

        // Calculate and store the unblock timestamp
        setUnblockTimestamp(Date.now() + blockingTimeInSeconds * 1000);

        start();
      }
    },
    [showBlockedPinErrorModal, clear, start, blockingTimeInSeconds, setUnblockTimestamp],
  );

  // Clear countdown when modal closes or on unmount
  useEffect(() => {
    if (!showBlockedPinErrorModal) {
      clear();
    }

    return () => {
      clear();
    };
  }, [showBlockedPinErrorModal, clear]);

  return (
    <Modal
      title="Tu PIN ha sido bloqueado"
      content={
        blockingTimeInSeconds === null ? (
          <>
            {BLOCKED_PIN_REASON} <BoldText>Verifica tu identidad</BoldText> para crear uno
            nuevo o espera a que se desbloquee.
            {providerText && (
              <>
                <br />
                <br />
                Mensaje del proveedor de firma: &quot;{providerText}&quot;
              </>
            )}
          </>
        ) : (
          <>
            {BLOCKED_PIN_REASON} <BoldText>Verifica tu identidad</BoldText> para crear uno
            nuevo o espera <BoldText>{formattedTime}</BoldText> para que se desbloquee
          </>
        )
      }
      isOpen={showBlockedPinErrorModal}
      buttonProps={{
        buttonText: "Verificar identidad",
        onClick: () => {
          clear();
          onReEnlistment();
          onPrimaryAction?.();
        },
        loading: isUnenrolling,
      }}
      secondaryButtonProps={{
        buttonText: "Volver a inicio",
        onClick: () => {
          clear();
          setShowBlockedPinErrorModal(false);
          onSecondaryAction?.();
        },
      }}
    />
  );
}
